import React from "react";
import { Button } from "@/components/ui/button";
import { CircleNotch, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Unified loading / error / empty state wrapper for any data-driven section.
 *
 * Usage:
 *   <DataState loading={loading} error={error} onRetry={load}
 *              empty={items.length === 0} emptyMessage="No formulations yet.">
 *     <Grid items={items} />
 *   </DataState>
 *
 * - loading: shows centered spinner card
 * - error:   shows branded retry card with the error message
 * - empty:   shows the provided emptyMessage (or children if omitted)
 * - otherwise: renders children
 *
 * Keeps the white-screen problem off of every fetch site.
 */
export default function DataState({
  loading,
  error,
  empty,
  emptyMessage,
  onRetry,
  loadingLabel = "Loading…",
  children,
  testId = "data-state",
}) {
  if (loading) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-stone-500 py-12"
        data-testid={`${testId}-loading`}
      >
        <CircleNotch size={18} className="animate-spin" />
        <span className="text-sm">{loadingLabel}</span>
      </div>
    );
  }

  if (error) {
    const msg =
      (error?.response?.data?.detail && typeof error.response.data.detail === "string"
        ? error.response.data.detail
        : null) ||
      error?.message ||
      "Couldn't load this section.";
    return (
      <div
        className="rounded-lg border border-stone-200 bg-white p-6 my-6 max-w-xl"
        data-testid={`${testId}-error`}
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-amber-50 text-amber-700 grid place-items-center shrink-0">
            <WarningCircle size={18} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-stone-900">
              Couldn't load this section.
            </div>
            <div className="text-sm text-stone-600 mt-1">
              {msg.length > 200 ? msg.slice(0, 200) + "…" : msg}
            </div>
            {onRetry && (
              <Button
                onClick={onRetry}
                size="sm"
                variant="outline"
                className="mt-3 border-stone-300"
                data-testid={`${testId}-retry`}
              >
                <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
                Try again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (empty && emptyMessage) {
    return (
      <div
        className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center my-6"
        data-testid={`${testId}-empty`}
      >
        <p className="text-stone-500 text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return children;
}
